import { motion } from "framer-motion";
import {
  Mail, Headphones, Calculator, Truck, Users, FileText, MapPin, ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: Mail, key: "remote",
    title: "Remote Office Administration",
    blurb: "Email, calendar, scheduling, document prep, data entry, customer database hygiene.",
    items: ["Email Management", "Calendar Management", "Appointment Scheduling", "Document Preparation", "Data Entry", "File Management", "Digital Filing", "Customer Database"],
    span: "lg:col-span-7 lg:row-span-2",
    img: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: Headphones, key: "support",
    title: "Customer Support",
    blurb: "Phone, email, live chat & follow-ups — handled professionally on your behalf.",
    items: ["Customer Email Support", "Phone Support", "Live Chat Support", "Appointment Confirmations", "Customer Follow-ups", "Complaint Resolution"],
    span: "lg:col-span-5",
  },
  {
    icon: Calculator, key: "books",
    title: "Bookkeeping Support",
    blurb: "Invoicing, AP/AR, expense tracking and QuickBooks data entry.",
    items: ["Invoice Preparation", "Accounts Receivable", "Accounts Payable", "Expense Tracking", "QuickBooks Data Entry", "Payment Follow-up"],
    note: "Not tax preparation or accounting advice.",
    span: "lg:col-span-5",
  },
  {
    icon: Truck, key: "trucking",
    title: "Trucking Office Administration",
    blurb: "Dispatch admin, driver files, safety docs, POD, fuel receipts & rate cons.",
    items: ["Dispatch Admin Support", "Driver File Management", "Safety Documentation", "POD Collection", "Fuel Receipt Organization", "Invoice Processing", "Rate Confirmation Management", "Fleet Documentation"],
    span: "lg:col-span-7",
    img: "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: Users, key: "hr",
    title: "HR & Payroll Administration",
    blurb: "Onboarding documents, timesheets, payroll prep and employee file management.",
    items: ["Employee Onboarding Documents", "Timesheet Management", "Payroll Preparation Support", "Employee File Management", "Vacation Tracking"],
    span: "lg:col-span-4",
  },
  {
    icon: FileText, key: "docs",
    title: "Document Management",
    blurb: "Physical & digital file organization, scanning, secure cloud filing.",
    items: ["Physical File Organization", "Digital File Organization", "Document Scanning", "Secure Cloud Filing", "Office Records Management"],
    span: "lg:col-span-4",
  },
  {
    icon: MapPin, key: "weekly",
    title: "Weekly Office Support",
    blurb: "On-site visits across the GTA — paperwork, filing, employee files, supplies.",
    items: ["Organize paperwork", "Maintain filing systems", "Scan documents", "Prepare employee files", "Restock office supplies", "Administrative projects"],
    note: "Available within the Greater Toronto Area.",
    span: "lg:col-span-4",
    accent: true,
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-28 lg:py-36 bg-secondary/40" data-testid="services-section">
      <div className="container-apex">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="overline">Our Services</div>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl mt-5 max-w-2xl">
              A complete <span className="text-[#16A34A]">back-office</span>, modular by the hour.
            </h2>
          </div>
          <p className="max-w-md text-muted-foreground">
            Mix and match the services you need. From a few hours per week to a full outsourced team — we scale with you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 auto-rows-fr">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.55 }}
                className={`card-apex group relative overflow-hidden ${s.span} ${s.accent ? "!bg-[#0B2545] !text-white !border-[#0B2545]" : ""}`}
                data-testid={`service-card-${s.key}`}
              >
                {s.img && (
                  <div className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity">
                    <img src={s.img} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/85 to-transparent" />
                  </div>
                )}
                <div className="relative p-8 h-full flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.accent ? "bg-[#16A34A]" : "bg-[#16A34A]/10 text-[#16A34A]"}`}>
                      <Icon size={22} className={s.accent ? "text-white" : ""} />
                    </div>
                    <ArrowUpRight className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all" size={20} />
                  </div>
                  <h3 className="mt-6 font-display font-bold text-2xl lg:text-3xl tracking-tight">{s.title}</h3>
                  <p className={`mt-3 text-sm ${s.accent ? "text-white/70" : "text-muted-foreground"}`}>{s.blurb}</p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {s.items.map((it) => (
                      <li
                        key={it}
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${s.accent ? "border-white/20 text-white/80" : "border-border text-foreground/70"}`}
                      >
                        {it}
                      </li>
                    ))}
                  </ul>
                  {s.note && (
                    <div className={`mt-5 text-[11px] uppercase tracking-widest font-bold ${s.accent ? "text-[#16A34A]" : "text-[#16A34A]"}`}>{s.note}</div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
